import React, { useMemo, useState } from "react";
import { Pressable, Text, View } from "react-native";

import { useAIExhibitFixedNarration } from "../audio/useAIExhibitFixedNarration";
import { AI_EXHIBIT_COLORS as C } from "../aiExhibitTheme";

const NARRATION_BY_EXAMPLE_ID = Object.freeze({
  pocket_calculator: "ai_or_not_pocket_calculator",
  spam_filter: "ai_or_not_spam_filter",
  industrial_robot_arm: "ai_or_not_industrial_robot_arm",
  music_recommendation: "ai_or_not_music_recommendation",
  automatic_sliding_door: "ai_or_not_sliding_door",
  chatgpt: "ai_or_not_chatgpt",
});

export default function AIOrNotAIScreen({
  copy,
  language = "en",
  audioEnabled = false,
}) {
  const { playNarration } = useAIExhibitFixedNarration({
    audioEnabled,
    language,
  });
  const [index, setIndex] = useState(0);
  const [selectedChoice, setSelectedChoice] = useState(null);
  const example = copy.aiOrNot.examples[index];

  const progress = useMemo(
    () => Math.round(((index + 1) / copy.aiOrNot.examples.length) * 100),
    [index, copy.aiOrNot.examples.length]
  );

  const nextExample = () => {
    setIndex((current) => (current + 1) % copy.aiOrNot.examples.length);
    setSelectedChoice(null);
  };

  return (
    <View style={{ padding: 28 }}>
      <Text
        style={{
          color: C.accent,
          fontSize: 13,
          fontWeight: "900",
          letterSpacing: 1.2,
        }}
      >
        {copy.aiOrNot.eyebrow}
      </Text>

      <Text
        style={{
          color: C.text,
          fontSize: 44,
          lineHeight: 50,
          fontWeight: "900",
          marginTop: 8,
        }}
      >
        {copy.aiOrNot.title}
      </Text>

      <Text
        style={{
          color: C.muted,
          fontSize: 19,
          lineHeight: 28,
          marginTop: 8,
        }}
      >
        {copy.aiOrNot.subtitle}
      </Text>

      <View
        style={{
          marginTop: 30,
          padding: 28,
          borderRadius: 22,
          borderWidth: 1,
          borderColor: C.border,
          backgroundColor: C.surfaceRaised,
        }}
      >
        <Text
          style={{
            color: C.muted,
            fontSize: 14,
            fontWeight: "800",
            textAlign: "center",
          }}
        >
          {index + 1} / {copy.aiOrNot.examples.length}
        </Text>

        <Text
          style={{
            color: C.text,
            fontSize: 38,
            fontWeight: "900",
            textAlign: "center",
            marginTop: 20,
            marginBottom: 26,
          }}
        >
          {example.label}
        </Text>

        <View style={{ flexDirection: "row", gap: 14, flexWrap: "wrap" }}>
          {copy.aiOrNot.choices.map((choice) => {
            const selected = selectedChoice === choice;
            return (
              <Pressable
                key={choice}
                onPress={() => {
                  setSelectedChoice(choice);
                  playNarration(NARRATION_BY_EXAMPLE_ID[example.id]);
                }}
                style={({ pressed }) => ({
                  flex: 1,
                  minWidth: 180,
                  minHeight: 76,
                  padding: 16,
                  borderRadius: 17,
                  borderWidth: selected ? 2 : 1,
                  borderColor: selected ? C.accent : C.border,
                  backgroundColor:
                    selected || pressed ? C.accentSoft : C.surface,
                  alignItems: "center",
                  justifyContent: "center",
                })}
              >
                <Text
                  style={{
                    color: C.text,
                    fontSize: 20,
                    fontWeight: "900",
                    textAlign: "center",
                  }}
                >
                  {choice}
                </Text>
              </Pressable>
            );
          })}
        </View>

        {selectedChoice ? (
          <View
            style={{
              marginTop: 24,
              padding: 20,
              borderRadius: 16,
              borderLeftWidth: 5,
              borderWidth: 1,
              borderColor: C.border,
              borderLeftColor: C.accent,
              backgroundColor: C.accentSoft,
            }}
          >
            <Text style={{ color: C.text, fontSize: 20, fontWeight: "900" }}>
              {example.answer}
            </Text>
            <Text
              style={{
                color: C.text,
                fontSize: 17,
                lineHeight: 26,
                marginTop: 9,
              }}
            >
              {example.explanation}
            </Text>

            <Pressable
              onPress={nextExample}
              style={({ pressed }) => ({
                alignSelf: "flex-start",
                marginTop: 18,
                minHeight: 52,
                paddingHorizontal: 18,
                paddingVertical: 12,
                borderRadius: 14,
                borderWidth: 1,
                borderColor: C.accent,
                backgroundColor: pressed ? C.surfaceRaised : C.surface,
              })}
            >
              <Text style={{ color: C.text, fontSize: 16, fontWeight: "900" }}>
                {copy.aiOrNot.another} →
              </Text>
            </Pressable>
          </View>
        ) : null}

        <View
          style={{
            marginTop: 24,
            height: 10,
            borderRadius: 999,
            backgroundColor: C.surface,
            overflow: "hidden",
          }}
        >
          <View
            style={{
              width: `${progress}%`,
              height: "100%",
              backgroundColor: C.accent,
            }}
          />
        </View>
      </View>
    </View>
  );
}
